import { onBeforeUnmount, onMounted, ref, watch } from "vue";

const BANNER_BODY_CLASS = "ipad-os-unsupported-banner-visible";

/** Safari/WebKit sometimes omits maxTouchPoints; undefined > 1 is false and wrongly rejects iPads. */
function hasLikelyTabletTouch(): boolean {
  if (typeof navigator === "undefined") return false;
  const mtp = navigator.maxTouchPoints;
  if (typeof mtp === "number" && !Number.isNaN(mtp) && mtp > 0) return true;
  return typeof window !== "undefined" && "ontouchstart" in window;
}

function uaCandidates(): string[] {
  if (typeof navigator === "undefined") return [];
  const ua = navigator.userAgent ?? "";
  const appVersion = navigator.appVersion ?? "";
  return ua === appVersion ? [ua] : [ua, appVersion].filter(Boolean);
}

export function isIPad(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent ?? "";

  // Classic mobile UA (always includes iPad).
  if (/iPad/i.test(ua)) return true;

  // Rare but reported on some WebKit builds when UA omits "iPad".
  if (navigator.platform === "iPad") return true;

  // iPhone / iPod — not targeted by this banner (avoid Macintosh heuristic overlap).
  if (/iPhone|iPod/i.test(ua)) return false;

  const touch = hasLikelyTabletTouch();
  if (!touch) return false;

  // iPadOS 13+ desktop-style UA: Macintosh + frozen Mac OS X 10_15.x.
  if (/Macintosh/i.test(ua)) return true;

  return navigator.platform === "MacIntel";
}

/** Extract major OS version from UA/appVersion segments Apple uses on iPad / desktop-iPad Safari. */
export function getIPadOSMajorVersion(): number | null {
  if (typeof navigator === "undefined") return null;

  const candidates = uaCandidates();

  for (const blob of candidates) {
    // "CPU OS 13_6", "CPU iPhone OS 13_6"; allow odd spacing from legacy parsers.
    const cpuOsMatch = blob.match(/CPU\s+iPhone\s+OS\s+(\d+)[._](\d+)/i);
    if (cpuOsMatch) return Number.parseInt(cpuOsMatch[1], 10);

    const cpuOsMatch2 = blob.match(/CPU\s+OS\s+(\d+)[._](\d+)/i);
    if (cpuOsMatch2) return Number.parseInt(cpuOsMatch2[1], 10);
  }

  const ua = navigator.userAgent ?? "";
  const touch = hasLikelyTabletTouch();

  const isDesktopPadUa =
    touch && /Macintosh/i.test(ua) && /Mac OS X 10[._]/i.test(ua);

  if (isDesktopPadUa) {
    const versionMatch = ua.match(/Version\/(\d+)/);
    if (versionMatch) return Number.parseInt(versionMatch[1], 10);
    return 15;
  } 

  for (const blob of candidates) {
    const legacy = blob.match(/OS (\d+)[._](\d+)/);
    if (legacy) return Number.parseInt(legacy[1], 10);
  }

  return null;
}

export function isUnsupportedIPadOS(minMajor = 16): boolean {
  if (!isIPad()) return false;
  const major = getIPadOSMajorVersion();
  if (major === null) return false;
  return major < minMajor;
}

export function useUnsupportedIPadOS(minMajorVersion = 16) {
  const isVisible = ref(false);
  const isIPadDevice = ref(false);
  const majorVersion = ref<number | null>(null);

  function syncBodyClass(visible: boolean) {
    if (!import.meta.client || typeof document === "undefined") return;
    document.body.classList.toggle(BANNER_BODY_CLASS, visible);
  }

  watch(isVisible, syncBodyClass);

  onMounted(() => {
    if (!import.meta.client) return;
    isIPadDevice.value = isIPad();
    majorVersion.value = getIPadOSMajorVersion();
    isVisible.value =
      isIPadDevice.value &&
      majorVersion.value !== null &&
      majorVersion.value < minMajorVersion;
  });

  onBeforeUnmount(() => {
    syncBodyClass(false);
  });

  return {
    isVisible,
    isIPad: isIPadDevice,
    majorVersion,
    minMajorVersion,
  };
}
